// A demo of a brand's wall, private, unlisted and marked as a demo everywhere; made to be shown
// in a sales conversation. When the brand's homepage could be opened, the wall sits on pictures
// of that page, between its top and its footer, for a computer or a phone (D-070). Otherwise it
// sits on a plain page in the brand's colors.
import type { PublicWall } from "@brandwall/contracts";
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router";
import { ApiError, api } from "../api";
import { WallPreview } from "../components/WallPreview";

interface SiteView {
  width: number;
  top: { src: string; height: number };
  bottom: { src: string; height: number } | null;
  background: string;
  text: string;
  wallWidth: number;
  font: string | null;
}

interface DemoData {
  brand: { name: string; website: string | null; logoUrl: string | null; colors: string[] };
  expiresAt: string;
  wall: PublicWall;
  site: { capturedAt: string; desktop: SiteView | null; phone: SiteView | null } | null;
  collecting: boolean;
  /** Screenshots of the wall on the brand's real homepage (D-076), and on a product page (D-088). */
  shots: {
    desktop: Shot | null;
    phone: Shot | null;
    product: { desktop: Shot | null; phone: Shot | null } | null;
  } | null;
}

interface Shot {
  src: string;
  width: number;
  height: number;
}

/** A screenshot of the wall on their real site, in a simple browser or phone frame. */
function Screenshot({
  d,
  shot,
  phone,
  where = "homepage",
}: {
  d: DemoData;
  shot: Shot;
  phone: boolean;
  where?: string;
}) {
  const host = d.brand.website?.replace(/^https?:\/\//, "").replace(/\/$/, "") ?? "";
  return (
    <div className={phone ? "demo-shot phone" : "demo-shot"}>
      {!phone && (
        <div className="demo-shot-bar">
          <i />
          <i />
          <i />
          <span>{host}</span>
        </div>
      )}
      <img
        src={shot.src}
        width={shot.width}
        height={shot.height}
        alt={`The Brandwall wall on ${d.brand.name}'s ${where}`}
      />
    </div>
  );
}

/** The width of an element, kept up to date. */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setWidth(el.clientWidth));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  return [ref, width] as const;
}

function OnTheirSite({ d, view, phone }: { d: DemoData; view: SiteView; phone: boolean }) {
  const [ref, width] = useWidth<HTMLDivElement>();
  // The pictures were taken at a fixed width; everything is scaled to fit, wall included.
  const zoom = width ? width / view.width : 1;
  const wall: PublicWall = {
    ...d.wall,
    design: {
      ...d.wall.design,
      heading: "",
      background: view.background,
      text: view.text,
      font: "inherit",
      ...(phone ? { columns: 2, rows: 3 } : {}),
    },
  };
  const page = (
    <div ref={ref}>
      <div style={{ width: view.width, zoom }}>
        <img
          className="shot"
          src={view.top.src}
          width={view.width}
          height={view.top.height}
          alt={`${d.brand.name}'s homepage`}
        />
        <div style={{ background: view.background, padding: phone ? "0 16px 40px" : "0 0 64px" }}>
          <div style={{ maxWidth: view.wallWidth, margin: "0 auto" }}>
            <WallPreview wall={wall} font={view.font ?? undefined} />
          </div>
        </div>
        {view.bottom && (
          <img
            className="shot"
            src={view.bottom.src}
            width={view.width}
            height={view.bottom.height}
            alt=""
          />
        )}
      </div>
    </div>
  );
  return phone && window.innerWidth >= 700 ? <div className="phone">{page}</div> : page;
}

export function Demo() {
  const { token } = useParams();
  const [d, setD] = useState<DemoData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [phone, setPhone] = useState(() => window.innerWidth < 700);
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow";
    document.head.append(meta);
    api<DemoData>(`/v1/public/demos/${token}`)
      .then((r) => {
        setD(r);
        document.title = `${r.brand.name} with Brandwall · demo`;
      })
      .catch((e) => setError(e instanceof ApiError ? e.message : "This demo isn't available."));
    return () => meta.remove();
  }, [token]);
  if (error) return <div className="onb">{error}</div>;
  if (!d) return <div className="onb muted">…</div>;
  const accent = d.brand.colors[0] ?? "#10302a";
  const view =
    d.site && (phone ? (d.site.phone ?? d.site.desktop) : (d.site.desktop ?? d.site.phone));
  const shot =
    d.shots && (phone ? (d.shots.phone ?? d.shots.desktop) : (d.shots.desktop ?? d.shots.phone));
  const product = d.shots?.product;
  const productShot =
    product && (phone ? (product.phone ?? product.desktop) : (product.desktop ?? product.phone));
  const both = d.shots
    ? (d.shots.desktop && d.shots.phone) || (product?.desktop && product.phone)
    : d.site?.desktop && d.site.phone;
  const flag = (
    <div className="demo-flag">
      <span>Demo by Brandwall · not affiliated with {d.brand.name} · private link</span>
      {(shot || productShot || view) && both && (
        <span className="demo-switch">
          <button type="button" aria-pressed={!phone} onClick={() => setPhone(false)}>
            Computer
          </button>
          <button type="button" aria-pressed={phone} onClick={() => setPhone(true)}>
            Phone
          </button>
        </span>
      )}
      <a href={`/demo/${token}/report`}>See the report</a>
    </div>
  );
  // Screenshots of the wall on their real homepage come first, then the wall to click through.
  if (shot || productShot)
    return (
      <div className="demo-site">
        {flag}
        {shot && <Screenshot d={d} shot={shot} phone={phone && Boolean(d.shots?.phone)} />}
        {productShot && (
          <>
            <h2 className="small muted" style={{ textAlign: "center", margin: "28px 0 14px" }}>
              On a product page
            </h2>
            <Screenshot
              d={d}
              shot={productShot}
              phone={phone && Boolean(product?.phone)}
              where="product page"
            />
          </>
        )}
        {d.wall.posts.length > 0 && (
          <main style={{ maxWidth: 1180, margin: "0 auto", padding: "8px 20px 60px" }}>
            <h2 className="small muted" style={{ textAlign: "center", margin: "0 0 14px" }}>
              The posts on the wall
            </h2>
            <WallPreview wall={{ ...d.wall, design: { ...d.wall.design, heading: "" } }} />
          </main>
        )}
      </div>
    );
  if (view && d.wall.posts.length)
    return (
      <div className="demo-site">
        {flag}
        <OnTheirSite
          key={phone ? "phone" : "desktop"}
          d={d}
          view={view}
          phone={phone && Boolean(d.site?.phone)}
        />
      </div>
    );
  return (
    <div style={{ background: "#fff", minHeight: "100vh" }}>
      {flag}
      <header className="demo-head" style={{ borderColor: accent }}>
        {d.brand.logoUrl ? (
          <img src={d.brand.logoUrl} alt={d.brand.name} style={{ maxHeight: 40 }} />
        ) : (
          <b style={{ letterSpacing: "0.15em" }}>{d.brand.name.toUpperCase()}</b>
        )}
        <span className="muted small">{d.brand.website?.replace(/^https?:\/\/(www\.)?/, "")}</span>
      </header>
      <div className="demo-hero" style={{ background: accent }} />
      <main style={{ maxWidth: 1180, margin: "0 auto", padding: "32px 20px 60px" }}>
        {d.wall.posts.length ? (
          <WallPreview
            wall={{
              ...d.wall,
              design: { ...d.wall.design, heading: d.wall.design.heading || "Seen on you" },
            }}
          />
        ) : (
          <p className="muted">
            {d.collecting
              ? "Brandwall is collecting customer posts and picking the ones about the brand. It takes a few minutes."
              : `No customer posts about ${d.brand.name} were found with these hashtags yet. Try hashtags people use for the brand itself.`}
          </p>
        )}
      </main>
    </div>
  );
}
